import type { InternalAxiosRequestConfig } from "axios";

import { mergeApis, Zodios } from "@zodios/core";
import axios, { isAxiosError } from "axios";
import qs from "qs";

import { normalizeAxiosError } from "@/api/api-error-normalization.ts";
import { tokenResponseSchema } from "@/api/backoffice/login/backoffice-external-login-api.ts";
import { kiliInternalRootApi } from "@/api/kili/internal/kili-internal-root-api.ts";
import { kiliOrganizationsApi } from "@/api/kili/organizations/kili-organizations-api.ts";
import { kiliProgrammingApi } from "@/api/kili/programming/kili-programming-api.ts";
import { kiliSitesApi } from "@/api/kili/sites/kili-sites-api.ts";
import { loadDeprecatedEnv } from "@/env.deprecated.ts";
import {
  retrieveTokenDetailsFromLocalStorage,
  saveTokenDetailsInLocalStorage,
} from "@/lib/helpers/authentication-helper.ts";
import { SessionExpiredError } from "@/lib/session-expired-error.ts";

const KiliApiDefinition = mergeApis({
  "/kli/voltacall/sites": kiliSitesApi,
  "/kli/voltacall/organizations": kiliOrganizationsApi,
  "/kli/voltacall/programing": kiliProgrammingApi,
  "/kli/voltacall": kiliInternalRootApi,
});

const depreactedEnv = loadDeprecatedEnv();

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const KiliApi = new Zodios(depreactedEnv.VITE_KILI_BASE_URL, KiliApiDefinition, {
  validate: depreactedEnv.VITE_ZODIOS_VALIDATE,
  axiosConfig: {
    withCredentials: true,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { arrayFormat: "comma" }),
    },
  },
});

KiliApi.axios.interceptors.request.use((config) => {
  const tokenDetails = retrieveTokenDetailsFromLocalStorage();
  if (!tokenDetails) {
    return config;
  }

  const { access_token } = tokenDetails;

  config.headers.set("Authorization", `Bearer ${access_token}`);

  return config;
});

KiliApi.axios.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  async (error) => {
    const tokenDetails = retrieveTokenDetailsFromLocalStorage();

    if (!tokenDetails) {
      throw normalizeAxiosError(error, "kili");
    }

    const { refresh_token: refreshToken } = tokenDetails;

    if (isAxiosError(error) && error.config) {
      const originalConfig = error.config as RetriableRequestConfig;

      if (error.response?.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;

        try {
          const resp = await axios.post(`${depreactedEnv.VITE_BACKOFFICE_BASE_URL}/login/refresh`, {
            refresh_token: refreshToken,
          });
          const refreshedTokenDetails = tokenResponseSchema.parse(resp.data);

          saveTokenDetailsInLocalStorage(refreshedTokenDetails);

          originalConfig.headers.Authorization = `Bearer ${refreshedTokenDetails.access_token}`;
          return await KiliApi.axios(originalConfig);
        } catch (refreshError) {
          if (isAxiosError(refreshError) && refreshError.response?.status === 401) {
            throw new SessionExpiredError(refreshError);
          }

          throw normalizeAxiosError(refreshError, "kili");
        }
      }
    }

    throw normalizeAxiosError(error, "kili");
  },
);

export default KiliApi;
